// frontend/src/components/p2p/tx-helpers.tsx
//
// Shared bits for rendering an on-chain transaction in trade/order rows:
//   - full hash + copy + explorer link (TxLine)
//   - placeholder line for hash-not-yet-available (PendingLine)
//   - labelled DD/MM/YYYY HH:MM:SS timestamp with ISO tooltip (Timestamp)
//
// Keeping these in one place avoids the drift that produced the previous
// "wer2scan.com/tx" vs "wer2scan.com/uni2/tx" inconsistency.
import { useState } from 'react'
import toast from 'react-hot-toast'
import { Check, Clock, Copy, ExternalLink } from 'lucide-react'

const EXPLORERS = {
  Uni2: 'https://wer2scan.com/uni2/tx/',
  BSC: 'https://bscscan.com/tx/',
} as const

export type SupportedChain = keyof typeof EXPLORERS

export function explorerTxUrl(chain: SupportedChain, hash: string): string {
  return `${EXPLORERS[chain]}${hash}`
}

export function TxLine({ chain, hash }: { chain: SupportedChain; hash: string }) {
  const [copied, setCopied] = useState(false)
  function copy() {
    if (!navigator.clipboard) return
    navigator.clipboard
      .writeText(hash)
      .then(() => {
        setCopied(true)
        toast.success(`Hash ${chain} copiado`)
        setTimeout(() => setCopied(false), 1500)
      })
      .catch(() => {})
  }
  const url = explorerTxUrl(chain, hash)
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-dark-700 bg-dark-800/40 text-[11px] font-mono">
      <span className="uppercase tracking-wider text-[10px] text-dark-400 shrink-0 w-12">{chain}</span>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="flex-1 min-w-0 truncate text-dark-300 hover:text-uni-400 transition-colors"
        title={hash}
      >
        {hash}
      </a>
      <button
        type="button"
        onClick={copy}
        className="shrink-0 text-dark-500 hover:text-dark-200 p-1"
        title="Copiar hash"
      >
        {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
      </button>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="shrink-0 text-dark-500 hover:text-uni-400 p-1"
        title="Abrir en explorer"
      >
        <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  )
}

export function PendingLine({ chain, label = 'pendiente — settlement on-chain no confirmado todavía' }: { chain: string; label?: string }) {
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-dashed border-dark-700 text-[11px] font-mono text-dark-500">
      <span className="uppercase tracking-wider text-[10px] shrink-0 w-12">{chain}</span>
      <Clock className="w-3 h-3" />
      <span>{label}</span>
    </div>
  )
}

export function Timestamp({ label, iso }: { label: string; iso: string | Date | null | undefined }) {
  if (!iso) {
    return (
      <span className="text-dark-600">
        <span className="text-dark-500 mr-1">{label}:</span>—
      </span>
    )
  }
  const d = iso instanceof Date ? iso : new Date(iso)
  return (
    <span title={d.toISOString()}>
      <span className="text-dark-500 mr-1">{label}:</span>
      {formatDateTime(d)}
    </span>
  )
}

export function formatDateTime(d: Date): string {
  // Locale fecha + hh:mm:ss. e.g. "12/05/2026 19:51:14"
  const date = d.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' })
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
  return `${date} ${time}`
}
